import { styled } from "@mui/material/styles";
import {
  ASSISTANT_DEFAULT_WIDTH,
  ASSISTANT_WIDTH_LARGE_STEP,
  ASSISTANT_WIDTH_STEP,
  assistantWidthLimits,
  clampAssistantWidth,
  type AssistantWidthLimits,
} from "ai-assistant/dock/assistant-column-width";
import { ASSISTANT_PANEL_ID } from "ai-assistant/dock/assistant-dock";
import { loadAssistantWidth, saveAssistantWidth } from "ai-assistant/dock/assistant-dock-preference";
import React from "react";

/**
 * The custom property the shell's assistant column reads its width from at `lg` and up. The handle
 * writes it straight onto the column, so a drag re-renders neither the shell nor the conversation.
 */
export const ASSISTANT_WIDTH_PROPERTY = "--assistant-column-width";

/** How far the drag target reaches over each side of the panel's edge, the 1px border it draws. */
const HANDLE_OVERHANG = 3;

const Handle = styled("div")(({ theme }) => ({
  bottom: 0,
  cursor: "col-resize",
  outline: 0,
  position: "absolute",
  right: -HANDLE_OVERHANG,
  // Below the app bar, which the column's top padding leaves room for.
  top: theme.spacing(8),
  touchAction: "none",
  width: HANDLE_OVERHANG * 2,
  // Above the page, which comes later in the row and would otherwise paint over the half beyond the edge.
  zIndex: 3,
  "&::after": {
    backgroundColor: "transparent",
    bottom: 0,
    content: '""',
    left: HANDLE_OVERHANG - 1,
    position: "absolute",
    top: 0,
    transition: "background-color 0.15s ease",
    width: 3,
  },
  "&:hover::after, &:focus-visible::after": {
    backgroundColor: theme.palette.primary.main,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

type AssistantResizeHandleProps = {
  /** Whose width this is. The shell keys the handle on it, so another user starts from their own. */
  userId?: string;
};

/**
 * The edge of the docked assistant beside the page, which a user drags to make it wider or narrower
 * (#7205). Drawn as the column's last child, so the column is its parent and the page the column's
 * next sibling: both are measured from the DOM rather than handed in as refs.
 *
 * The width a user chooses is kept as they chose it. What is drawn is that width clamped to the room
 * the window has now, so a width chosen on a large monitor comes back when the window is large again,
 * and never pushes the page under its minimum on a small one.
 */
export default function AssistantResizeHandle(props: Readonly<AssistantResizeHandleProps>) {
  const { userId } = props;
  const handleRef = React.useRef<HTMLDivElement | null>(null);
  const preferredRef = React.useRef(ASSISTANT_DEFAULT_WIDTH);
  const widthRef = React.useRef(ASSISTANT_DEFAULT_WIDTH);
  const pointerIdRef = React.useRef<number | null>(null);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(ASSISTANT_DEFAULT_WIDTH);

  const measureLimits = React.useCallback((): AssistantWidthLimits | undefined => {
    const column = handleRef.current?.parentElement;
    const row = column?.parentElement;
    const page = column?.nextElementSibling;
    if (!column || !row || !page) return undefined;

    const sharedWidth = column.getBoundingClientRect().width + page.getBoundingClientRect().width;
    return assistantWidthLimits(row.getBoundingClientRect().width, sharedWidth);
  }, []);

  /** Draws `width`, clamped to the room there is, and returns what was drawn. */
  const applyWidth = React.useCallback((width: number): number => {
    const handle = handleRef.current;
    const limits = measureLimits();
    if (!handle || !limits) return widthRef.current;

    const drawn = clampAssistantWidth(width, limits);
    widthRef.current = drawn;
    handle.parentElement?.style.setProperty(ASSISTANT_WIDTH_PROPERTY, `${drawn}px`);
    handle.setAttribute("aria-valuemin", `${limits.min}`);
    handle.setAttribute("aria-valuemax", `${limits.max}`);
    handle.setAttribute("aria-valuenow", `${drawn}`);
    return drawn;
  }, [measureLimits]);

  /** Makes `width` the user's choice, draws it, and keeps it. */
  const chooseWidth = React.useCallback((width: number) => {
    const drawn = applyWidth(width);
    preferredRef.current = drawn;
    saveAssistantWidth(userId, drawn);
  }, [applyWidth, userId]);

  React.useLayoutEffect(() => {
    preferredRef.current = loadAssistantWidth(userId) ?? ASSISTANT_DEFAULT_WIDTH;
    applyWidth(preferredRef.current);
  }, [applyWidth, userId]);

  React.useEffect(() => {
    const row = handleRef.current?.parentElement?.parentElement;
    // Back towards the user's choice whenever the room changes: narrower when the window shrinks,
    // and wide again when it grows back.
    const fitToRoom = () => {
      if (pointerIdRef.current !== null) return;
      applyWidth(preferredRef.current);
    };

    let rowObserver: ResizeObserver | undefined;
    if (row && typeof ResizeObserver !== "undefined") {
      rowObserver = new ResizeObserver(fitToRoom);
      rowObserver.observe(row);
    }
    globalThis.addEventListener("resize", fitToRoom);
    return () => {
      rowObserver?.disconnect();
      globalThis.removeEventListener("resize", fitToRoom);
    };
  }, [applyWidth]);

  /** Ends a drag. The body's cursor and text selection come back even when there is no handle left. */
  const stopDragging = React.useCallback((handle: HTMLDivElement | null) => {
    if (handle && pointerIdRef.current !== null && handle.hasPointerCapture(pointerIdRef.current)) {
      handle.releasePointerCapture(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }, []);

  React.useEffect(() => {
    // Taken now: closing the assistant mid-drag unmounts the handle, and React has cleared the ref
    // by the time this cleanup runs (review of #7230).
    const handle = handleRef.current;
    return () => {
      if (pointerIdRef.current === null) return;
      stopDragging(handle);
    };
  }, [stopDragging]);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    startWidthRef.current = widthRef.current;
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    event.preventDefault();
  }, []);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    // The page is to the right, so dragging right makes the assistant wider.
    applyWidth(startWidthRef.current + (event.clientX - startXRef.current));
  }, [applyWidth]);

  const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
    // Saved once, when the drag is released.
    if (widthRef.current !== startWidthRef.current) chooseWidth(widthRef.current);
  }, [chooseWidth, stopDragging]);

  const handlePointerCancel = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
    applyWidth(startWidthRef.current);
  }, [applyWidth, stopDragging]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? ASSISTANT_WIDTH_LARGE_STEP : ASSISTANT_WIDTH_STEP;
    let nextWidth: number | undefined;

    if (event.key === "ArrowLeft") nextWidth = widthRef.current - step;
    if (event.key === "ArrowRight") nextWidth = widthRef.current + step;
    if (event.key === "Home") nextWidth = 0;
    if (event.key === "End") nextWidth = Number.POSITIVE_INFINITY;
    if (nextWidth === undefined) return;

    event.preventDefault();
    chooseWidth(nextWidth);
  }, [chooseWidth]);

  const handleDoubleClick = React.useCallback(() => {
    preferredRef.current = ASSISTANT_DEFAULT_WIDTH;
    applyWidth(ASSISTANT_DEFAULT_WIDTH);
    saveAssistantWidth(userId, undefined);
  }, [applyWidth, userId]);

  return (
    <Handle
      aria-controls={ASSISTANT_PANEL_ID}
      aria-label="Resize the assistant"
      aria-orientation="vertical"
      onDoubleClick={handleDoubleClick}
      onFocus={() => applyWidth(preferredRef.current)}
      onKeyDown={handleKeyDown}
      onLostPointerCapture={(event) => {
        if (pointerIdRef.current === event.pointerId) stopDragging(event.currentTarget);
      }}
      onPointerCancel={handlePointerCancel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      ref={handleRef}
      role="separator"
      tabIndex={0}
      title="Drag or use the arrow keys to resize. Double-click to reset."
    />
  );
}
